/**
 * Chatbot Billing Service
 * Wraps billing API calls needed inside the chatbot:
 *  - load billing config (to get rateListId / rateTypeId)
 *  - search services by name (with optional category filter)
 */
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, forkJoin } from 'rxjs';
import { map, catchError, switchMap, tap } from 'rxjs/operators';
import { AuthService } from 'app/auth/auth.service';

export interface ServiceOption {
  serviceId: number;
  serviceName: string;
  categoryName?: string;
  deptName?: string;
  rate?: number;
  maxDiscountLimit?: number;
}

export interface PatientLookupResult {
  valid: boolean;
  patientId?: number;
  patientName?: string;
  partyId?: number;
  doctorId?: number;
  ipdId?: number;
  isExpired?: boolean;
  error?: string;
}

export interface DoctorOption {
  userId: number;
  userName: string;
}

export interface DepartmentOption {
  id: number;
  name: string;
}

/** Payment mode from Common/GetPaymenttype → FK_PayTypeId on receipts */
export interface PaymentTypeOption {
  id: number;
  name: string;
}

@Injectable({ providedIn: 'root' })
export class ChatbotBillingService {

  private configLoaded = false;
  private rateListId   = 1;
  private rateTypeId   = 1;

  constructor(private http: HttpClient, private auth: AuthService) {}

  /** Validate UHID and retrieve patient registration IDs from the main API. */
  lookupPatient(uhid: string): Observable<PatientLookupResult> {
    const id = parseInt(String(uhid).replace(/\D/g, ''), 10);
    if (!id || id <= 0) {
      return of({ valid: false, error: 'Invalid UHID format.' });
    }

    const url = `${this.auth.newbaseUrl}PatientRegistration/GetPatient/${id}`;
    return this.http.get<any>(url).pipe(
      map(data => {
        if (!data || !data.pkRegId || data.pkRegId <= 0) {
          return { valid: false, error: 'Patient not registered. Please check the UHID.' };
        }
        if (data.isExpired === true) {
          return { valid: false, error: 'This patient record is marked as expired.' };
        }
        const first = (data.firstName || '').trim();
        const last  = (data.lastName || '').trim();
        const name  = [first, last].filter(Boolean).join(' ') || 'Unknown';
        return {
          valid:       true,
          patientId:   data.pkRegId,
          patientName: name,
          partyId:     data.fkPartyId || null,
          doctorId:    data.fK_DoctorId || null,
          ipdId:       data.fkIPDID || null,
          isExpired:   false,
        };
      }),
      catchError(err => {
        const msg = err?.error?.message || err?.statusText || 'Could not verify UHID.';
        return of({ valid: false, error: msg });
      })
    );
  }

  /** Load doctor departments (sub-specialities) — same API as billing screen. */
  getDepartments(specId: number = 0): Observable<DepartmentOption[]> {
    const url = `${this.auth.newbaseUrl}Common/GetSubSpeciality/${specId}/${this.auth.Loggeduser.specialityId}`;
    return this.http.get<any[]>(url).pipe(
      map(data => (data || [])
        .map(d => ({
          id:   d.pK_SubSpecialityId || d.pkSubSpecialityId || 0,
          name: (d.subSpecialityName || d.SubSpecialityName || '').trim(),
        }))
        .filter(d => d.id > 0 && d.name)
      ),
      catchError(() => of([]))
    );
  }

  /** Load doctors for a selected department (sub-speciality id). */
  getDoctors(subSpecialityId: number): Observable<DoctorOption[]> {
    const date     = this._formatDate(this.auth.getServerDate);
    const branchId = this.auth.Loggeduser.branchid;
    const specId   = this.auth.Loggeduser.specialityId;
    const deptId   = subSpecialityId > 0 ? subSpecialityId : 0;
    const url      = `${this.auth.newbaseUrl}VisitMaster/GetUserListBySubSpecialityId/6`
                   + `/${branchId}/${date}/6/${deptId}/${specId}`;

    return this.http.get<any[]>(url).pipe(
      map(data => {
        let list = data || [];
        if (this.auth.masterdata?.isExternal === true) {
          list = list.filter(d => d.isExternal === false);
        }
        return list
          .map(d => ({ userId: d.userId, userName: (d.userName || '').trim() }))
          .filter(d => d.userId > 0 && d.userName);
      }),
      catchError(() => of([]))
    );
  }

  private _formatDate(d: Date | string): string {
    const dt  = d instanceof Date ? d : new Date(d);
    const y   = dt.getFullYear();
    const m   = this._pad2(dt.getMonth() + 1);
    const day = this._pad2(dt.getDate());
    return `${y}-${m}-${day}`;
  }

  private _pad2(n: number): string {
    return n < 10 ? '0' + n : String(n);
  }

  /** Load billing config once to get default rateListId / rateTypeId */
  private ensureConfig(): Observable<void> {
    if (this.configLoaded) return of(undefined);
    const url = `${this.auth.newbaseUrl}Billing/GetBillingConfigSetup/${this.auth.Loggeduser.branchid}`;
    return this.http.get<any[]>(url).pipe(
      tap(data => {
        if (data?.[0]) {
          this.rateListId = data[0].fK_RatelistId || 1;
          this.rateTypeId = data[0].fK_RatelistId || 1;
        }
        this.configLoaded = true;
      }),
      map(() => undefined),
      catchError(() => { this.configLoaded = true; return of(undefined); })
    );
  }

  /**
   * Search services by name from the billing rate list.
   * Returns up to 10 matched services sorted by relevance.
   */
  searchServices(query: string, categoryName?: string): Observable<ServiceOption[]> {
    if (!query?.trim()) return of([]);

    return this.ensureConfig().pipe(
      switchMap(() => {
        const q   = encodeURIComponent(query.trim());
        const cat = encodeURIComponent(categoryName?.trim() || '~');
        // searchtype S = search by service name; ~ = wildcard for dept/category
        const url = `${this.auth.newbaseUrl}RateList/GetRateListByRateListIdRateTypeIdByDeptNameByCategoryNameByServiceName`
                  + `/${this.rateListId}/${this.rateTypeId}/~/${cat}/${q}/S`;

        return this.http.get<any[]>(url).pipe(
          map(data => this._rank(query, data || [])),
          catchError(() => this._fallbackSearch(query))
        );
      })
    );
  }

  /** If category search returns nothing, try without category filter */
  private _fallbackSearch(query: string): Observable<ServiceOption[]> {
    const q   = encodeURIComponent(query.trim());
    const url = `${this.auth.newbaseUrl}RateList/GetRateListByRateListIdRateTypeIdByDeptNameByCategoryNameByServiceName`
              + `/${this.rateListId}/${this.rateTypeId}/~/~/${q}/S`;
    return this.http.get<any[]>(url).pipe(
      map(data => this._rank(query, data || [])),
      catchError(() => of([]))
    );
  }

  /** Sort results by relevance score and return top 8 */
  private _rank(query: string, list: any[]): ServiceOption[] {
    if (!list.length) return [];

    const norm = (s: string) => (s || '').toLowerCase().replace(/[.\-\/()&,;:]+/g, ' ').trim();
    const q    = norm(query);

    const scored = list.map(item => {
      const name = norm(item.serviceName || '');
      let score  = 0;
      if (name === q)          score = 100;
      else if (name.includes(q)) score = 80;
      else if (q.includes(name)) score = 60;
      else {
        const qWords = q.split(' ').filter(Boolean);
        const nWords = name.split(' ').filter(Boolean);
        const hits   = qWords.filter(qw => nWords.some(nw =>
          nw === qw || nw.startsWith(qw) || qw.startsWith(nw) ||
          (qw.length >= 5 && nw.length >= 5 && qw.slice(0, 5) === nw.slice(0, 5))
        )).length;
        score = Math.round((hits / Math.max(qWords.length, 1)) * 50);
      }
      return { item, score };
    });

    const ranked = scored
      .filter(x => x.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 8);

    const top = ranked.length ? ranked : scored.sort((a, b) => b.score - a.score).slice(0, 8);

    return top.map(x => ({
        serviceId:        x.item.serviceId   || x.item.rateListDetailId || 0,
        serviceName:      x.item.serviceName || '',
        categoryName:     x.item.categoryName || '',
        deptName:         x.item.deptName    || '',
        rate:             x.item.rate        || x.item.Rate || 0,
        maxDiscountLimit: x.item.maxDiscountLimit || 0,
      }));
  }

  /** Load payment modes configured in the billing module (Cash, Card, UPI, etc.). */
  getPaymentTypes(): Observable<PaymentTypeOption[]> {
    const url = `${this.auth.newbaseUrl}Common/GetPaymenttype`;
    return this.http.get<any[]>(url).pipe(
      map(list => (list || [])
        .filter(t => t?.pK_Id != null)
        .map(t => ({ id: Number(t.pK_Id), name: String(t.name || '').trim() }))
        .filter(t => t.name.length > 0)
      ),
      catchError(() => of([
        { id: 1, name: 'Cash' },
        { id: 2, name: 'Card' },
        { id: 3, name: 'Online' },
      ]))
    );
  }
}
